import type { Fingering } from '@shared-types/fingering';
import { type Tuning, STANDARD_TUNING, tuningPrefersSharps } from '@theory/tunings';
import { midiToPitchClass, pitchClassToSemi } from '@util/midi';
import { INTERVAL_BY_SEMITONE } from '@theory/intervals';
import styles from './ChordDiagram.module.css';

const NUM_FRETS = 6;

function noteColour(
  stringIdx: number,
  fret: number,
  tuning: Tuning,
  chordRoot: string | undefined,
  fallback: string,
): string {
  if (!chordRoot || fret < 0) return fallback;
  const stringMidi = tuning.midi[stringIdx];
  const noteSemi = (stringMidi + fret) % 12;
  const rootSemi = pitchClassToSemi(chordRoot);
  const semitones = ((noteSemi - rootSemi) + 12) % 12;
  return INTERVAL_BY_SEMITONE[semitones]?.colour ?? fallback;
}

interface ChordDiagramProps {
  fingering: Fingering;
  width?: number;
  primaryColor?: string;
  showFingerNumbers?: boolean;
  isLeftHanded?: boolean;
  tuning?: Tuning;
  showStringNames?: boolean;
  /** Chord root used to colour each note dot by its interval. */
  chordRoot?: string;
}

export function ChordDiagram({
  fingering,
  width = 160,
  primaryColor = 'var(--colour-accent)',
  showFingerNumbers = true,
  isLeftHanded = false,
  tuning = STANDARD_TUNING,
  showStringNames = true,
  chordRoot,
}: ChordDiagramProps) {
  const stringCount = tuning.midi.length;
  if (fingering.frets.length !== stringCount) return null;

  const padH = width * 0.12;
  const padTop = width * 0.18;
  const padBottom = showStringNames ? width * 0.18 : width * 0.1;
  const gridW = width - padH * 2;
  const gridH = width * 1.08;
  const stringSpacing = gridW / (stringCount - 1);
  const fretSpacing = gridH / NUM_FRETS;
  const dotR = stringSpacing * 0.3;

  // If the entire fingering fits within the first 6 frets, anchor the diagram
  // at the nut (fret 0) regardless of the library's startFret hint, and label
  // frets 3 and 5 as positional reference points. Otherwise honour startFret.
  const playedFrets = fingering.frets.filter((f) => f > 0);
  const maxPlayedFret = playedFrets.length > 0 ? Math.max(...playedFrets) : 0;
  const fitsInFirstSix = maxPlayedFret > 0 && maxPlayedFret <= 6;
  const effectiveStartFret = fitsInFirstSix ? 1 : fingering.startFret;
  const isFirstFret = effectiveStartFret === 1;
  const labelMargin = isFirstFret ? 0 : width * 0.12;
  const padLeft = padH + labelMargin;
  const totalWidth = width + labelMargin;
  const height = padTop + gridH + padBottom;
  const REFERENCE_FRETS = [3, 5];

  const sharps = tuningPrefersSharps(tuning);
  const stringLabels = Array.from({ length: stringCount }, (_, i) =>
    midiToPitchClass(tuning.midi[i], sharps),
  );
  const strings = isLeftHanded ? [...stringLabels].reverse() : stringLabels;
  const frets = isLeftHanded ? [...fingering.frets].reverse() : fingering.frets;
  const fingerNums = isLeftHanded ? [...fingering.fingers].reverse() : fingering.fingers;
  const originalIndex = (visualIdx: number) =>
    isLeftHanded ? stringCount - 1 - visualIdx : visualIdx;

  const sx = (i: number) => padLeft + i * stringSpacing;
  const fy = (fret: number) => padTop + (fret - effectiveStartFret + 0.5) * fretSpacing;

  const barreInfo = fingering.barre;

  return (
    <svg
      className={styles.svg}
      width={totalWidth}
      height={height}
      viewBox={`0 0 ${totalWidth} ${height}`}
      role="img"
      aria-label={fingering.label}
    >
      {!isFirstFret && (
        <text
          x={padLeft - 16}
          y={padTop + fretSpacing * 0.5}
          fill="var(--colour-text-secondary)"
          fontSize={width * 0.09}
          fontFamily="var(--font-mono-family)"
          textAnchor="end"
          dominantBaseline="middle"
        >
          {effectiveStartFret}
        </text>
      )}

      {isFirstFret &&
        REFERENCE_FRETS.map((f) => (
          <text
            key={`fref-${f}`}
            x={padLeft - 10}
            y={fy(f)}
            fill="var(--colour-text-muted)"
            fontSize={width * 0.075}
            fontFamily="var(--font-mono-family)"
            textAnchor="end"
            dominantBaseline="middle"
          >
            {f}
          </text>
        ))}

      {isFirstFret && (
        <rect
          x={padLeft}
          y={padTop - 3}
          width={gridW}
          height={6}
          fill="var(--colour-text-primary)"
          rx={2}
        />
      )}

      {Array.from({ length: NUM_FRETS + 1 }).map((_, i) => (
        <line
          key={`fret-${i}`}
          x1={padLeft}
          y1={padTop + i * fretSpacing}
          x2={padLeft + gridW}
          y2={padTop + i * fretSpacing}
          stroke="var(--colour-border)"
          strokeWidth={i === 0 && isFirstFret ? 0 : 1.5}
        />
      ))}

      {Array.from({ length: stringCount }).map((_, i) => (
        <line
          key={`str-${i}`}
          x1={sx(i)}
          y1={padTop}
          x2={sx(i)}
          y2={padTop + gridH}
          stroke="var(--colour-text-muted)"
          strokeWidth={1}
        />
      ))}

      {barreInfo && (() => {
        const fromStr = isLeftHanded ? stringCount - barreInfo.toString : barreInfo.fromString - 1;
        const toStr = isLeftHanded ? stringCount - barreInfo.fromString : barreInfo.toString - 1;
        const x1 = sx(fromStr);
        const x2 = sx(toStr);
        const cy = fy(barreInfo.fret);
        return (
          <rect
            x={Math.min(x1, x2) - dotR * 0.5}
            y={cy - dotR}
            width={Math.abs(x2 - x1) + dotR}
            height={dotR * 2}
            rx={dotR}
            fill="var(--colour-text-secondary)"
            opacity={0.5}
          />
        );
      })()}

      {frets.map((fret, i) => {
        const cx = sx(i);
        const stringIdxForColour = originalIndex(i);
        const dotColour = noteColour(stringIdxForColour, fret, tuning, chordRoot, primaryColor);

        if (fret === -1) {
          return (
            <text
              key={`m-${i}`}
              x={cx}
              y={padTop - dotR * 1.4}
              fill="var(--colour-text-secondary)"
              fontSize={dotR * 1.6}
              fontFamily="var(--font-mono-family)"
              fontWeight={700}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              X
            </text>
          );
        }

        if (fret === 0) {
          return (
            <text
              key={`o-${i}`}
              x={cx}
              y={padTop - dotR * 1.4}
              fill={dotColour}
              fontSize={dotR * 1.6}
              fontFamily="var(--font-mono-family)"
              fontWeight={700}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              O
            </text>
          );
        }

        return (
          <g key={`dot-${i}`}>
            <circle cx={cx} cy={fy(fret)} r={dotR} fill={dotColour} />
            {showFingerNumbers && fingerNums[i] > 0 && (
              <text
                x={cx}
                y={fy(fret)}
                fill="var(--colour-background)"
                fontSize={dotR * 1.1}
                fontFamily="var(--font-label-family)"
                fontWeight={500}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {fingerNums[i]}
              </text>
            )}
          </g>
        );
      })}

      {showStringNames &&
        strings.map((note, i) => (
          <text
            key={`name-${i}`}
            x={sx(i)}
            y={padTop + gridH + padBottom * 0.6}
            fill="var(--colour-text-muted)"
            fontSize={width * 0.08}
            fontFamily="var(--font-mono-family)"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {note}
          </text>
        ))}
    </svg>
  );
}
